import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://danni.narodnichitalishta.bg/api";

export const fetchNationalAverages = createAsyncThunk(
  "municipalities/fetchNationalAverages",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/national-averages`);
      return response.data;
    } catch (error) {
      console.error("Error fetching national averages:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch municipalities from DATABASE (за MunicipalitiesPage)
export const fetchMunicipalities = createAsyncThunk(
  "municipalities/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Fetching municipalities from DB...");
      console.log("API URL:", `${BASE_URL}/municipalities`);
      const response = await axios.get(`${BASE_URL}/municipalities`);
      console.log(`✓ Loaded ${response.data.length} municipalities from DB`);
      return response.data;
    } catch (error) {
      console.error("Error fetching municipalities:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch municipalities GeoJSON (за BulgarianMap)
export const fetchMunicipalitiesGeoJson = createAsyncThunk(
  "municipalities/fetchGeoJson",
  async (_, { rejectWithValue }) => {
    try {
      console.log("Fetching GeoJSON for map...");
      const geoJsonResponse = await axios.get(
        `${BASE_URL}/geocoding/municipalities`
      );

      const municipalities = geoJsonResponse.data.features.map(
        (feature, index) => ({
          id: feature.properties.nuts4 || index,
          municipalityCode: feature.properties.nuts4, // nuts4 е municipality_code
          code: feature.properties.nuts4,
          name: feature.properties.name || feature.properties.nuts4,
          nuts3: feature.properties.nuts3,
          nuts4: feature.properties.nuts4,
          geometry: feature.geometry,
          properties: feature.properties,
        })
      );

      console.log(
        `✓ Loaded ${municipalities.length} municipalities from GeoJSON`
      );
      return municipalities;
    } catch (error) {
      console.error("Error fetching GeoJSON:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch municipality by code (nuts4/municipality_code)
export const fetchMunicipalityByCode = createAsyncThunk(
  "municipalities/fetchByCode",
  async (municipalityCode, { rejectWithValue }) => {
    try {
      console.log("Fetching municipality:", municipalityCode);
      const response = await axios.get(
        `${BASE_URL}/municipalities/${municipalityCode}`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching municipality:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch municipality metrics
export const fetchMunicipalityMetrics = createAsyncThunk(
  "municipalities/fetchMetrics",
  async (municipalityCode, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/municipalities/${municipalityCode}/metrics`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching metrics:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fetch municipality chitalishta
export const fetchMunicipalityChitalishta = createAsyncThunk(
  "municipalities/fetchChitalishta",
  async (municipalityCode, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/municipalities/${municipalityCode}/chitalishta`
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching chitalishta:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Search municipalities
export const searchMunicipalities = createAsyncThunk(
  "municipalities/search",
  async (query, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/municipalities/search`, {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      console.error("Error searching municipalities:", error);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const municipalitySlice = createSlice({
  name: "municipalities",
  initialState: {
    municipalities: [], // От DB (за списък)
    municipalitiesGeoJson: [], // От GeoJSON (за карта)
    currentMunicipality: null,
    municipalityMetrics: null,
    chitalishta: [],
    searchQuery: "",
    loading: false,
    geoJsonLoading: false,
    metricsLoading: false,
    chitalishtaLoading: false,
    error: null,
  },
  reducers: {
    clearCurrentMunicipality: (state) => {
      state.currentMunicipality = null;
      state.municipalityMetrics = null;
      state.chitalishta = [];
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNationalAverages.pending, (state) => {
        state.averagesLoading = true;
      })
      .addCase(fetchNationalAverages.fulfilled, (state, action) => {
        state.averagesLoading = false;
        state.nationalAverages = action.payload;
      })
      .addCase(fetchNationalAverages.rejected, (state, action) => {
        state.averagesLoading = false;
        state.error = action.payload;
      })
      // Fetch municipalities from DB
      .addCase(fetchMunicipalities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMunicipalities.fulfilled, (state, action) => {
        state.loading = false;
        state.municipalities = action.payload;
      })
      .addCase(fetchMunicipalities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch GeoJSON for map
      .addCase(fetchMunicipalitiesGeoJson.pending, (state) => {
        state.geoJsonLoading = true;
        state.error = null;
      })
      .addCase(fetchMunicipalitiesGeoJson.fulfilled, (state, action) => {
        state.geoJsonLoading = false;
        state.municipalitiesGeoJson = action.payload;
      })
      .addCase(fetchMunicipalitiesGeoJson.rejected, (state, action) => {
        state.geoJsonLoading = false;
        state.error = action.payload;
      })

      // Fetch municipality by code
      .addCase(fetchMunicipalityByCode.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMunicipalityByCode.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMunicipality = action.payload;
      })
      .addCase(fetchMunicipalityByCode.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch metrics
      .addCase(fetchMunicipalityMetrics.pending, (state) => {
        state.metricsLoading = true;
      })
      .addCase(fetchMunicipalityMetrics.fulfilled, (state, action) => {
        state.metricsLoading = false;
        state.municipalityMetrics = action.payload;
      })
      .addCase(fetchMunicipalityMetrics.rejected, (state, action) => {
        state.metricsLoading = false;
        state.error = action.payload;
      })

      // Fetch chitalishta
      .addCase(fetchMunicipalityChitalishta.pending, (state) => {
        state.chitalishtaLoading = true;
      })
      .addCase(fetchMunicipalityChitalishta.fulfilled, (state, action) => {
        state.chitalishtaLoading = false;
        state.chitalishta = action.payload;
      })
      .addCase(fetchMunicipalityChitalishta.rejected, (state, action) => {
        state.chitalishtaLoading = false;
        state.error = action.payload;
      })

      // Search municipalities
      .addCase(searchMunicipalities.pending, (state) => {
        state.loading = true;
      })
      .addCase(searchMunicipalities.fulfilled, (state, action) => {
        state.loading = false;
        state.municipalities = action.payload;
      })
      .addCase(searchMunicipalities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearCurrentMunicipality, setSearchQuery } =
  municipalitySlice.actions;
export default municipalitySlice.reducer;
